/* LIQUID_GOLD: Rally at Sherwood Village — JavaScript core.
   The single global for the whole theme. Every later phase adds behaviour via
   LiquidGold.register('name', init) in its own liquid_gold_<feature>.js —
   never a bare DOM script. */

(function () {
  'use strict';

  window.LiquidGold = window.LiquidGold || {};
  var LG = window.LiquidGold;

  LG.modules = LG.modules || {};
  LG.teardowns = LG.teardowns || {};

  /* LIQUID_GOLD: honour the OS-level reduced-motion preference everywhere.
     Read live rather than cached so the theme editor reflects a change. */
  LG.reducedMotion = function () {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  /**
   * Register a module.
   * @param {string} name    Unique module name.
   * @param {function} init  init(root) — called with a container element.
   * @param {function} [teardown] teardown(root) — called on section unload.
   */
  LG.register = function (name, init, teardown) {
    if (typeof init !== 'function') return;
    LG.modules[name] = init;
    if (typeof teardown === 'function') LG.teardowns[name] = teardown;

    // LIQUID_GOLD: a module registered after first paint still needs to run.
    if (LG._booted) {
      try {
        init(document);
      } catch (err) {
        console.error('[LiquidGold] ' + name + ' init failed', err);
      }
    }
  };

  /** Run every registered module over a root element. */
  LG.init = function (root) {
    var scope = root || document;
    Object.keys(LG.modules).forEach(function (name) {
      try {
        LG.modules[name](scope);
      } catch (err) {
        // One broken module must never stop the others.
        console.error('[LiquidGold] ' + name + ' init failed', err);
      }
    });
  };

  /** Run every registered teardown over a root element. */
  LG.destroy = function (root) {
    var scope = root || document;
    Object.keys(LG.teardowns).forEach(function (name) {
      try {
        LG.teardowns[name](scope);
      } catch (err) {
        console.error('[LiquidGold] ' + name + ' teardown failed', err);
      }
    });
  };

  /* ---------------------------------------------------------------
     Shared reveal observer
     Elements with [data-lg-reveal] gain .lg-reveal--in at 20% visibility,
     once. One observer for the whole page, not one per section.
     --------------------------------------------------------------- */
  var revealObserver = null;

  function revealAll(scope) {
    var els = scope.querySelectorAll('[data-lg-reveal]');
    for (var i = 0; i < els.length; i++) els[i].classList.add('lg-reveal--in');
  }

  LG.register('reveal', function (root) {
    var scope = root || document;

    // No IntersectionObserver, or reduced motion: show everything immediately
    // so content is never trapped behind an animation that will not run.
    if (!('IntersectionObserver' in window) || LG.reducedMotion()) {
      revealAll(scope);
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('lg-reveal--in');
            revealObserver.unobserve(entry.target);
          });
        },
        // LIQUID_GOLD: 0.15 matches the mockup's per-section observers.
        { threshold: 0.15 }
      );
    }

    var els = scope.querySelectorAll('[data-lg-reveal]:not(.lg-reveal--in)');
    for (var i = 0; i < els.length; i++) revealObserver.observe(els[i]);
  });

  /* ---------------------------------------------------------------
     Count-up — animate a number from 0 to its target on reveal.
     Cubic ease-out, matching the mockup's stat behaviour.
     --------------------------------------------------------------- */

  /**
   * @param {Element} el      Element whose textContent is replaced each frame.
   * @param {number} target   Final value.
   * @param {object} [opts]   {duration, prefix, suffix, decimals}
   * @returns {function} cancel
   */
  LG.countUp = function (el, target, opts) {
    var o = opts || {};
    var duration = o.duration || 1400;
    var prefix = o.prefix || '';
    var suffix = o.suffix || '';
    var decimals = o.decimals || 0;
    var raf = null;

    function render(value) {
      el.textContent = prefix + value.toFixed(decimals) + suffix;
    }

    // LIQUID_GOLD: reduced motion still needs the final number, just not the count.
    if (LG.reducedMotion()) {
      render(target);
      return function () {};
    }

    var start = null;

    function step(now) {
      if (start === null) start = now;
      var p = Math.min((now - start) / duration, 1);
      // cubic ease-out, as in the mockup: 1 - (1 - p)^3
      render(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = window.requestAnimationFrame(step);
    }

    raf = window.requestAnimationFrame(step);

    return function cancel() {
      if (raf) window.cancelAnimationFrame(raf);
    };
  };

  /* ---------------------------------------------------------------
     Focus trap — shared by every modal, drawer, and mega menu
     --------------------------------------------------------------- */
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]),' +
    ' textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /**
   * Trap focus inside a container until the returned function is called.
   * @param {Element} container
   * @returns {function} release — restores focus to the previously active element.
   */
  LG.trapFocus = function (container) {
    var previous = document.activeElement;

    function onKeydown(event) {
      if (event.key !== 'Tab') return;
      var nodes = container.querySelectorAll(FOCUSABLE);
      if (!nodes.length) return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKeydown);

    var firstFocusable = container.querySelector(FOCUSABLE);
    if (firstFocusable) firstFocusable.focus();

    return function release() {
      container.removeEventListener('keydown', onKeydown);
      if (previous && typeof previous.focus === 'function') previous.focus();
    };
  };

  /* ---------------------------------------------------------------
     Body scroll lock — reference-counted so nested overlays behave
     --------------------------------------------------------------- */
  var lockCount = 0;

  // LIQUID_GOLD: overflow: hidden alone does not stop programmatic scrolling,
  // so the Lenis instance (liquid_gold_scroll.js, when loaded) is halted too —
  // otherwise the wheel would still move the page behind an open drawer.
  LG.lockScroll = function () {
    lockCount += 1;
    if (lockCount === 1) {
      document.body.style.overflow = 'hidden';
      if (LG.scroll) LG.scroll.stop();
    }
  };

  LG.unlockScroll = function () {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      document.body.style.overflow = '';
      if (LG.scroll) LG.scroll.start();
    }
  };

  /* ---------------------------------------------------------------
     Storage helpers — never throw in private mode or with storage blocked
     --------------------------------------------------------------- */
  LG.storage = {
    get: function (key) {
      try {
        return window.localStorage.getItem(key);
      } catch (err) {
        return null;
      }
    },
    set: function (key, value) {
      try {
        window.localStorage.setItem(key, value);
        return true;
      } catch (err) {
        return false;
      }
    },
    session: {
      get: function (key) {
        try {
          return window.sessionStorage.getItem(key);
        } catch (err) {
          return null;
        }
      },
      set: function (key, value) {
        try {
          window.sessionStorage.setItem(key, value);
          return true;
        } catch (err) {
          return false;
        }
      }
    }
  };

  /* ---------------------------------------------------------------
     rAF-throttled scroll subscription — one listener for the whole theme.
     Sections that track scroll progress subscribe here instead of adding
     their own listener.
     --------------------------------------------------------------- */
  var scrollSubs = [];
  var ticking = false;

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      for (var i = 0; i < scrollSubs.length; i++) {
        try {
          scrollSubs[i]();
        } catch (err) {
          console.error('[LiquidGold] scroll subscriber failed', err);
        }
      }
      ticking = false;
    });
  }

  LG.onScroll = function (fn) {
    if (typeof fn !== 'function') return function () {};
    scrollSubs.push(fn);
    if (scrollSubs.length === 1) {
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
    }
    fn();
    return function unsubscribe() {
      var i = scrollSubs.indexOf(fn);
      if (i > -1) scrollSubs.splice(i, 1);
      if (!scrollSubs.length) {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      }
    };
  };

  /* ---------------------------------------------------------------
     Boot
     --------------------------------------------------------------- */
  function boot() {
    LG._booted = true;
    LG.init(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // LIQUID_GOLD: theme editor — re-initialise on section load, tear down on unload.
  document.addEventListener('shopify:section:load', function (event) {
    LG.init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    LG.destroy(event.target);
  });
})();
